import {
  QueriedData,
  showErrorNotification,
  showSuccessNotification,
} from '../assets/useAssets';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useIntl } from 'react-intl';
import {
  Colaborator,
  ColaboratorWriteModel,
  createColaborator,
  deleteColaborator,
  getAllColaborators,
  inviteColaborator,
  updateColaborator,
} from 'src/api/colaborators';

export interface UseColaboratorsResult {
  colaboratorsQuery: QueriedData<Colaborator[]>;
  createColaborator: (colaborator: ColaboratorWriteModel) => void;
  updateColaborator: (data: {
    id: string;
    colaborator: ColaboratorWriteModel;
  }) => void;
  archiveColaborator: (colaboratorID: string) => void;
  inviteColaborator: (id: string) => void;
}

function useCreateColaborator() {
  const queryClient = useQueryClient();
  const intl = useIntl();
  const { mutate: create } = useMutation(
    (colaborator: ColaboratorWriteModel) => createColaborator(colaborator),
    {
      onSuccess: () => {
        showSuccessNotification('colaborator-create-success-message', intl);
        queryClient.invalidateQueries(['colaborators']);
      },
      onError: () => {
        showErrorNotification('colaborator-create-fail-message', intl);
      },
    },
  );
  return create;
}

export function useColaborators(): UseColaboratorsResult {
  const query = useQuery({
    queryFn: async () => getAllColaborators(),
    queryKey: ['colaborators'],
  });

  const create = useCreateColaborator();
  const queryClient = useQueryClient();
  const intl = useIntl();
  const { mutate: archive } = useMutation(
    (colaboratorID: string) => deleteColaborator(colaboratorID),
    {
      onSuccess: () => {
        showSuccessNotification('colaborator-archive-success-message', intl);
        queryClient.invalidateQueries(['colaborators']);
      },
      onError: () =>
        showErrorNotification('colaborator-archive-fail-message', intl),
    },
  );
  const updateColaborator = useUpdateColaborator();
  const inviteColaborator = useInviteColaborator();
  return {
    colaboratorsQuery: query,
    createColaborator: create,
    archiveColaborator: archive,
    updateColaborator,
    inviteColaborator,
  };
}

export function useUpdateColaborator() {
  const queryClient = useQueryClient();
  const intl = useIntl();

  const { mutate: update } = useMutation(
    ({ id, colaborator }: { id: string; colaborator: ColaboratorWriteModel }) =>
      updateColaborator(id, colaborator),
    {
      onSuccess: (_, { id }) => {
        showSuccessNotification('colaborator-update-success-message', intl);
        queryClient.invalidateQueries(['colaborators', id]);
        queryClient.invalidateQueries(['colaborators']);
      },
      onError: () => {
        showErrorNotification('colaborator-update-fail-message', intl);
      },
    },
  );
  return update;
}

export function useInviteColaborator() {
  const queryClient = useQueryClient();
  const intl = useIntl();

  const { mutate: inviteColaboratorMutation } = useMutation({
    mutationFn: (id: string) => inviteColaborator(id),
    onSuccess: (_, id) => {
      showSuccessNotification('colaborator-invite-success-message', intl);
      queryClient.invalidateQueries(['colaborators', id]);
      queryClient.invalidateQueries(['colaborators']);
    },
    onError: () => {
      showErrorNotification('colaborator-invite-fail-message', intl);
    },
  });
  return inviteColaboratorMutation;
}
